import { ChakraProvider } from "@chakra-ui/react";
import { Global } from "@emotion/react";
import {
  AuthContextProvider,
  CustomCSSReset,
  LanguageProvider,
  theme,
} from "@questy/aura-cl";
import React, { Suspense, useEffect, useState } from "react";
import ReactDOM, { createPortal } from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import AppAux from "./AppAux";
import SettingsContextProvider from "./context/SettingsContext";
import globalStyles from "./globalStyles";
import "./i18n";

export const queryClient = new QueryClient();

export const auraSpaContextUsedSettings = [
  "allow_register_individual",
  "available_currencies",
  "available_languages",
  "block_order_with_exceeded_quantity",
  "company_short_name_enabled",
  "cookie_notice",
  "country_code_selector",
  "create_new_accounts",
  "currency_active",
  "datera_server_address",
  "default_currency",
  "default_language",
  "delivery_cost",
  "discount_apply_to_product_types",
  "discount_currency",
  "discount_precision",
  "discount_scope_types",
  "display_company_in_commission_calendar_and_timeline",
  "display_company_in_task_calendar",
  "document_position_drag_and_drop",
  "limit_serviced_products_to_customer",
  "new_campaign_opportunity_first_field",
  "new_discount_system",
  "new_look_campaign_opportunity",
  "new_look_account_user_modal",
  "new_look_commission",
  "new_look_commission_modal",
  "new_look_company_modal",
  "new_look_discount_list",
  "new_look_document_offer_question",
  "new_look_document_order",
  "new_look_email_message",
  "new_look_list_filter_as_tag",
  "new_look_list_mobile_view",
  "new_look_system_communicator",
  "new_look_price_individual_list",
  "new_look_product",
  "new_look_task",
  "new_look_wrap_lines_in_lists",
  "product_autocomplete_addittional_data",
  "sell_by_price",
  "show_image_in_autocomplete",
  "show_no_products",
  "two_step_login",
  "unique_serviced_product_serial",
  "validate_storehouse_content_for_commission_position",
  "new_look_change_log",
  "currency_from_product",
  "validate_nip_on_create_or_edit",
];

const getPageMountingPoint = () => {
  return document.getElementById("aura-spa-root");
};

const BaseApp = () => {
  // stan potrzebny tylko do wywołania rerender BaseApp, żeby zamontował się portal
  const [, setRerender] = useState<any>(null);

  useEffect(() => {
    const targetNode = document.body;
    const config = { childList: true, subtree: true };
    const callback: MutationCallback = (_, observer) => {
      const mainMountingPoint = getPageMountingPoint();
      if (
        mainMountingPoint &&
        !mainMountingPoint.classList.contains("react-mounted")
      ) {
        setRerender(new Date());
        mainMountingPoint.classList.add("react-mounted");
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <React.StrictMode>
      <BrowserRouter>
        <ChakraProvider theme={theme} resetCSS={false}>
          <CustomCSSReset />
          <Global styles={globalStyles} />
          <QueryClientProvider client={queryClient}>
            <AuthContextProvider>
              <LanguageProvider>
                <SettingsContextProvider settings={auraSpaContextUsedSettings}>
                  <Suspense fallback={null}>
                    {document.getElementById("aura-spa-root")
                      ? createPortal(
                          <App />,
                          document.getElementById(
                            "aura-spa-root"
                          ) as HTMLElement
                        )
                      : null}
                    {createPortal(
                      <AppAux />,
                      document.getElementById(
                        "aura-spa-aux-root"
                      ) as HTMLElement
                    )}
                  </Suspense>
                </SettingsContextProvider>
              </LanguageProvider>
            </AuthContextProvider>
          </QueryClientProvider>
        </ChakraProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
};

ReactDOM.render(<BaseApp />, document.getElementById("aura-spa"));
